// KundenPortalSettings.jsx
// Pixel-aligned to your screenshot. Uses Ant Design ColorPicker.
// Make sure you import AntD base styles once in your app root:
import 'antd/dist/reset.css';

import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Paper,
    Typography,
    Grid,
    TextField,
    Stack,
    IconButton,
    InputAdornment,
    Divider,
    Tooltip,
    Button,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import AddIcon from "@mui/icons-material/Add";
import { ColorPicker } from "antd";
import ScopeHeader from '../../../components/ScopeHeader';

import { ReactComponent as CameraSvg } from '../../../assets/Camera.svg';
import CustomAddressHelpDialog from '../CustomAddressHelpDialog';

const SECTION_PX = { px: 3, py: 3 };
const BORDER = "#E6E8EB";
const TITLE = "var(--color-primary)";
const SUBTLE = "rgba(var(--color-primary-rgb), 0.88)";

/* ----------------------- UploadBox (matches screenshot) ----------------------- */
function UploadBox({ text = "", onChange,logo }) {
    return (
        <label style={{ display: "inline-block", width: "100%", maxWidth: 360 }}>
            <input
                type="file"
                accept="image/*"
                hidden
                onChange={onChange}
            />
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 1,
                    px: 2,
                    py: 1.25,
                    cursor: "pointer",
                    transition: "border-color .15s ease",
                    "&:hover": { borderColor: "#D4D8DC" },
                }}
            >
                <Box sx={{ position: "relative", width: 22, height: 22 }}>
                    <CameraSvg />

                </Box>

                <Typography
                    sx={{
                        color: "#678795",
                        fontSize: 16,
                        fontWeight: 400,
                        letterSpacing: 0.15,
                    }}
                >
                    {text}
                </Typography>

                {logo && (
                    <Box
                        component="img"
                        alt="Logo"
                        src={logo}
                        sx={{ maxHeight: 22, maxWidth: 160, objectFit: "contain" }}
                    />
                )}
            </Box>
        </label>
    );
}

/* --------------------------------- Main --------------------------------- */
export default function Tab2() {
    const i18n = useSelector((state) => state.i18n);
    const [logo, setLogo] = useState(null);
    const [primaryHex, setPrimaryHex] = useState("#FDC40E");
    const [customUrl, setCustomUrl] = useState("");
    const [dirtyBrand, setDirtyBrand] = useState(false);
    const [dirtyUrl, setDirtyUrl] = useState(false);

    const [addressHelpOpen, setAddressHelpOpen] = useState(false);

    const onPickLogo = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setLogo(URL.createObjectURL(f));
        setDirtyBrand(true);
    };

    const onPickColor = (c) => {
        const hex = c?.toHexString?.() || primaryHex;
        setPrimaryHex(hex);
        setDirtyBrand(true);
    };

    const onHexManual = (e) => {
        let v = e.target.value.trim();
        if (!v.startsWith("#")) v = `#${v}`;
        setPrimaryHex(v.toUpperCase());
        setDirtyBrand(true);
    };

    const saveBrand = () => setDirtyBrand(false);
    const saveUrl = () => setDirtyUrl(false);

    const previewBtnHover = useMemo(() => primaryHex, [primaryHex]);

    function hexToRgba(hex, alpha = 1) {
        let c = hex.replace("#", "");
        if (c.length === 3) {
            c = c.split("").map((ch) => ch + ch).join("");
        }
        const bigint = parseInt(c, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `rgba(${r},${g},${b},${alpha})`;
    }


    return (
        <Box sx={{ p: 3 }}>

            <CustomAddressHelpDialog open={addressHelpOpen} onClose={() => { setAddressHelpOpen(false) }} />
            {/* PAGE TITLE */}
            <Typography variant="h6" sx={{ fontWeight: 500, color: TITLE, my: 1 }}>
                {i18n?.customerPortal_settingsTitle}
            </Typography>
            <Typography className='textSecondary' sx={{ mb: 3 }}>
                {i18n?.customerPortal_settingsDescription}
            </Typography>

            {/* --- SECTION: Darstellung --- */}
            <Paper variant="outlined" sx={{ borderColor: BORDER, borderRadius: 2, mb: 3 }}>
                <Box {...SECTION_PX}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, color: TITLE, mb: 2 }}>
                        {i18n?.customerPortal_displayTitle}
                    </Typography>

                    <Grid container spacing={3} alignItems="center">
                        {/* Logo */}
                        <Grid item xs={12} md={6}>
                            <Typography className='textSecondaryBlack' sx={{ mb: 1.5, fontWeight: 500 }}>
                                {i18n?.customerPortal_logoLabel}
                            </Typography>

                            <Stack direction="row" spacing={2} alignItems="center">
                            <UploadBox text={i18n?.customerPortal_logoUploadLabel} onChange={onPickLogo} logo={logo}/>

                            </Stack>
                        </Grid>

                        {/* Primärfarbe */}
                        <Grid item xs={12} md={6}>
                            <Typography className='textSecondaryBlack' sx={{ mb: 1.5, fontWeight: 500 }}>
                                {i18n?.customerPortal_primaryColorLabel}
                            </Typography>

                            <Grid container spacing={1.5} alignItems="center">

                                <Grid item xs>
                                    <TextField
                                        value={primaryHex.replace("#", "")}
                                        onChange={onHexManual}
                                        fullWidth
                                        size="small"
                                        label={i18n?.customerPortal_hexLabel}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">#</InputAdornment>
                                            ),
                                            endAdornment: (
                                                <ColorPicker value={primaryHex} onChange={onPickColor}>
                                                    <Box
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: 1,
                                                            bgcolor: primaryHex,
                                                            border: "1px solid rgba(0,0,0,0.15)",
                                                            cursor: "pointer",
                                                        }}
                                                    />
                                                </ColorPicker>
                                            )
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>

                {/* PREVIEW */}
                <Divider />
                <Box {...SECTION_PX} sx={{ pt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, color: TITLE, mb: 1.5 }}>
                        {i18n?.customerPortal_previewTitle}
                    </Typography>

                    {/* banner preview */}
                    {/* <Box
                        sx={{
                            border: `1px solid ${BORDER}`,
                            borderRadius: 1,
                            overflow: "hidden",
                            mb: 2,
                        }}
                    >
                        <Box
                            sx={{
                                py: 2,
                                px: 2,
                                position: "relative",
                                color: "#fff",
                                background: `
        linear-gradient(0deg, ${hexToRgba(primaryHex, 0.6)}, ${hexToRgba(primaryHex, 0.6)}),
        url("/images/bg1.webp"),
        ${primaryHex}
      `,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                {i18n?.customerPortal_bannerTitle}
                            </Typography>
                            <Typography>
                                {i18n?.customerPortal_bannerDescription}
                            </Typography>
                        </Box>
                    </Box> */}

                    <ScopeHeader
                        width={"820px"}
                        height={"153px"}
                        bgColor={primaryHex}
                        linearGradient={"rgba(255, 255, 255, 0.4)"}
                        bgImage="../images/bg1.webp"
                        txtColor="#FFFFFF"
                        title={i18n?.customerPortal_bannerTitle}
                        id="card_dashboard_main"
                        dataTestId="card_dashboard_main"
                        description={i18n?.customerPortal_bannerDescription}
                        contentColor="#000000"
                    />




                    {/* preview controls row */}
                    <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 1 }}>
                        <Typography sx={{ fontWeight: 500, color: TITLE }}>
                            {i18n?.customerPortal_previewHeadline}
                        </Typography>
                        <Typography sx={{ color: primaryHex, textDecoration: "underline", cursor: "pointer" }}>
                            {i18n?.customerPortal_previewLinks}
                        </Typography>
                        <Typography sx={{ color: primaryHex }}>{i18n?.customerPortal_previewDefaultText}</Typography>

                        <Button
                            variant="contained"
                            size="small"
                            sx={{
                                bgcolor: primaryHex,
                                color: "#ffffff",
                                fontWeight: 500,
                                px: 1.75,
                                "&:hover": { bgcolor: previewBtnHover },
                            }}
                        >
                            {i18n?.customerPortal_previewPrimaryButton}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            sx={{
                                borderColor: primaryHex,
                                color: primaryHex,
                                fontWeight: 500,
                                px: 1.75,
                                "&:hover": { borderColor: primaryHex },
                            }}
                        >
                            {i18n?.customerPortal_previewSecondaryButton}
                        </Button>
                    </Stack>

                    <Divider sx={{ mt: 4 }} />


                    <Button
                        variant="contained"
                        disabled={!dirtyBrand}
                        onClick={saveBrand}
                        sx={{
                            mt: 1.5,
                            bgcolor: dirtyBrand ? TITLE : "rgba(0,0,0,0.12)",
                            color: dirtyBrand ? "#fff" : "rgba(0,0,0,0.38)",
                            boxShadow: "none",
                            px: 2,
                        }}
                    >
                        {i18n?.customerPortal_saveChanges}
                    </Button>
                </Box>
            </Paper>

            {/* --- SECTION: URL --- */}
            <Paper variant="outlined" sx={{ borderColor: BORDER, borderRadius: 2 }}>
                <Box {...SECTION_PX}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, color: TITLE, mb: 0.5 }}>
                        {i18n?.customerPortal_portalUrlTitle}
                    </Typography>
                    <Typography className='textSecondary' sx={{ mb: 2 }}>
                        {i18n?.customerPortal_portalUrlDescription}{" "}
                        {i18n?.customerPortal_portalUrlDescriptionSuffix} <span className='cp' onClick={() => { setAddressHelpOpen(true) }} style={{ color: SUBTLE }}>{i18n?.customerPortal_openGuideLink}.</span>
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>

                            <TextField
                                label={i18n?.customerPortal_standardAddressLabel}
                                style={{ width: "49%" }}
                                value="https://cat.247access.io/"
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                edge="end"
                                                onClick={() => navigator.clipboard.writeText("https://cat.247access.io/")}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                            />
                            <Typography className='textSecondary' sx={{ display: "block", mt: 0.75 }}>
                                {i18n?.customerPortal_standardAddressNote}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={i18n?.customerPortal_customAddressLabel}
                                placeholder={i18n?.customerPortal_customAddressPlaceholder}
                                value={customUrl}
                                onChange={(e) => {
                                    setCustomUrl(e.target.value);
                                    setDirtyUrl(true);
                                }}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                edge="end"
                                        onClick={() => navigator.clipboard.writeText("https://cat.247access.io/")}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Typography className='textSecondary' sx={{ display: "block", mt: 0.75 }}>
                                {i18n?.customerPortal_customAddressExample}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Divider sx={{ mt: 2 }} />

                    <Button
                        variant="contained"
                        disabled={!dirtyUrl}
                        onClick={saveUrl}
                        sx={{
                            mt: 2.5,
                            bgcolor: dirtyUrl ? TITLE : "rgba(0,0,0,0.12)",
                            color: dirtyUrl ? "#fff" : "rgba(0,0,0,0.38)",
                            boxShadow: "none",
                            px: 2,
                        }}
                    >
                        {i18n?.customerPortal_saveChanges}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
